// @school-apps/core/calendar — the pure .ics emitter (I·1). Hub records in, RFC 5545 text out.
// PURE: no HTTP, no DB, no clock — the caller injects the record arrays and the DTSTAMP. That is
// what makes the output deterministic for a fixed input (snapshot-testable) and lets I·2/I·3 serve
// the same bytes from the authenticated download and the token feed.
//
// Scope (the hub's shape, DATA-CONVENTIONS § 1): assignments (`asn_`, date-only `due`) and exams
// (`exm_`, date-only `date`) become all-day VEVENTs (VALUE=DATE — a due date is a day, not a time).
// Records without a valid date are SKIPPED (a VEVENT requires DTSTART; inventing a date would be a
// guessed value). Done assignments stay in — the calendar shows the semester, not a todo list.
// UIDs are stable from the record ids, so a calendar app that re-fetches the feed updates events
// in place instead of duplicating them.
//
// CAL·5 — personal events (`pev_`) also ride the calendar (download + token feed). They reuse CAL·1's
// http-free validateEvent as the skip-gate (a rejected event is dropped, like a malformed meeting),
// then emit one of four VEVENT shapes (one-off/recurring × all-day/timed).

import { validateEvent } from "../hub/events-validate.js";

const CRLF = "\r\n";
const encoder = new TextEncoder();

/**
 * Escape a TEXT property value per RFC 5545 § 3.3.11: backslash, semicolon, comma, and newlines.
 * @param {unknown} v
 * @returns {string}
 */
export function escapeText(v) {
  return String(v ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n|\r|\n/g, "\\n");
}

/**
 * Fold one content line at 75 OCTETS (not chars) per RFC 5545 § 3.1: continuation lines start
 * with a single space and the fold never splits a UTF-8 sequence (we measure per character).
 * @param {string} line a single logical content line (no CRLF)
 * @returns {string} the folded physical line(s), CRLF-joined, without a trailing CRLF
 */
export function foldLine(line) {
  const out = [];
  let cur = "";
  let curBytes = 0;
  let limit = 75; // continuation lines lose one octet to the leading space
  for (const ch of line) {
    const b = encoder.encode(ch).length;
    if (curBytes + b > limit) {
      out.push(cur);
      cur = " " + ch;
      curBytes = 1 + b;
      limit = 75;
    } else {
      cur += ch;
      curBytes += b;
    }
  }
  out.push(cur);
  return out.join(CRLF);
}

/** YYYY-MM-DD (date-only ISO, the hub's `due`/`date` format) -> 20260716; null if not a date. */
function icsDate(v) {
  const m = typeof v === "string" ? v.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
  return m ? `${m[1]}${m[2]}${m[3]}` : null;
}

/** ISO-8601 UTC (core now() format) -> 20260716T050000Z; throws on junk (the stamp is required). */
function icsStamp(iso) {
  const m = typeof iso === "string" ? iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/) : null;
  if (!m) throw new Error(`buildCalendar: stamp must be an ISO-8601 UTC string, got ${JSON.stringify(iso)}`);
  return `${m[1]}${m[2]}${m[3]}T${m[4]}${m[5]}${m[6]}Z`;
}

// ── SCH·1 — timed recurring class meetings ──────────────────────────────────────────────────────
// All three kids are US Central. Hardcoded, ONE place (hard constraint), never per-user detection.
export const SCHEDULE_TZID = "America/Chicago";

// The shared VTIMEZONE for America/Chicago with BOTH observances (US rules since 2007): DAYLIGHT
// (CDT, UTC-5) begins the 2nd Sunday of March 02:00 local; STANDARD (CST, UTC-6) begins the 1st
// Sunday of November 02:00 local. A client applies these to keep a 3:00pm class at 3:00pm on both
// sides of the November transition. Emitted ONCE per calendar, only when there are meetings.
const VTIMEZONE_CHICAGO = [
  "BEGIN:VTIMEZONE",
  `TZID:${SCHEDULE_TZID}`,
  "BEGIN:DAYLIGHT",
  "TZOFFSETFROM:-0600",
  "TZOFFSETTO:-0500",
  "TZNAME:CDT",
  "DTSTART:19700308T020000",
  "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU",
  "END:DAYLIGHT",
  "BEGIN:STANDARD",
  "TZOFFSETFROM:-0500",
  "TZOFFSETTO:-0600",
  "TZNAME:CST",
  "DTSTART:19701101T020000",
  "RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU",
  "END:STANDARD",
  "END:VTIMEZONE",
];

// The weekday-code → JS getUTCDay() index map (SU=0…SA=6). Exported so CAL·2's occurrence expander
// reuses the SAME codes instead of re-deriving them (a second copy could silently drift).
export const DAY_WDAY = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
const DAY_ORDER = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"]; // week order for a stable BYDAY list

/**
 * Parse a STRICT "YYYY-MM-DD" into a UTC-midnight Date, rejecting non-calendar dates (month 13,
 * day 32). Returns null on anything malformed. Exported for CAL·2's window math + date validation;
 * `icsDate`/`firstOccurrence` keep their own lenient prefix regexes so ics's output is unchanged.
 * @param {unknown} v
 * @returns {Date | null}
 */
export function parseIsoDate(v) {
  const m = typeof v === "string" ? v.match(/^(\d{4})-(\d{2})-(\d{2})$/) : null;
  if (!m) return null;
  const [y, mo, d] = [+m[1], +m[2], +m[3]];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d ? dt : null;
}
const isTime = (t) => typeof t === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(t);
const hhmm = (t) => `${t.slice(0, 2)}${t.slice(3, 5)}00`; // "15:30" -> "153000"

/** The first calendar date on/after `termStartISO` whose weekday is `dayCode` -> "YYYYMMDD" (UTC date math). */
function firstOccurrence(termStartISO, dayCode) {
  const m = typeof termStartISO === "string" ? termStartISO.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
  if (!m || !(dayCode in DAY_WDAY)) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  d.setUTCDate(d.getUTCDate() + ((DAY_WDAY[dayCode] - d.getUTCDay() + 7) % 7));
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, "0")}${String(d.getUTCDate()).padStart(2, "0")}`;
}

/**
 * Build the RFC 5545 calendar text for a kid's hub.
 * @param {object} input
 * @param {Array<object>} [input.courses] hub course payloads (used for the "(course)" summary suffix)
 * @param {Array<object>} [input.assignments] hub assignment payloads ({ id, courseId, title, due })
 * @param {Array<object>} [input.exams] hub exam payloads ({ id, courseId, title, date, type? })
 * @param {Array<object>} [input.events] CAL·5 personal-event payloads (`pev_`: { id, title, kind,
 *   color?, note?, one-off `date` XOR recurring `days`+`from`+`to`, optional `start`/`end` time window }).
 *   Each is run through CAL·1's validateEvent skip-gate and emitted as a VEVENT (X-SCHOOLAPPS-KIND:event);
 *   a rejected event is dropped, like a malformed meeting.
 * @param {string} input.stamp DTSTAMP for every event, ISO-8601 UTC — injected, never read from a clock
 * @param {string} [input.name] X-WR-CALNAME (calendar display name)
 * @param {string} [input.prodId] PRODID override (defaults to the school-apps identifier)
 * @returns {string} CRLF-terminated, 75-octet-folded VCALENDAR text
 */
export function buildCalendar({ courses = [], assignments = [], exams = [], meetings = [], events = [], stamp, name, prodId } = {}) {
  const dtstamp = icsStamp(stamp);
  const courseName = new Map(courses.map((c) => [c.id, c.name]));
  const suffix = (courseId) => {
    const n = courseName.get(courseId);
    return n ? ` (${n})` : "";
  };

  // SCH·1 — group class meetings that are identical EXCEPT for the weekday into ONE recurring VEVENT
  // (BYDAY=MO,WE for a MW class). A meeting missing a valid day/time/term is dropped (it can't emit a
  // valid RRULE — inventing one would be a guessed value). Deterministic: the group's DTSTART anchors
  // to the earliest first-occurrence among its days; the UID is the group's minimum member id.
  const groups = new Map();
  for (const m of meetings) {
    if (!m || !m.id || !(m.day in DAY_WDAY) || !isTime(m.start) || !isTime(m.end)) continue;
    if (!firstOccurrence(m.termStart, m.day) || !icsDate(m.termEnd)) continue;
    const key = [m.courseId ?? "", m.start, m.end, m.room ?? "", m.instructor ?? "", m.kind ?? "", m.termStart, m.termEnd].join(" ");
    if (!groups.has(key)) groups.set(key, { sample: m, days: new Set(), ids: [] });
    const g = groups.get(key);
    g.days.add(m.day);
    g.ids.push(m.id);
  }
  const meetingEvents = [...groups.values()].map((g) => {
    const m = g.sample;
    const days = DAY_ORDER.filter((d) => g.days.has(d));
    const uid = [...g.ids].sort()[0];
    const firstYmd = days.map((d) => firstOccurrence(m.termStart, d)).sort()[0]; // earliest occurrence anchors DTSTART
    const until = `${icsDate(m.termEnd)}T235959Z`; // termEnd as a UTC datetime (RFC 5545: UNTIL is UTC when DTSTART has a TZID)
    const kindLabel = m.kind && m.kind !== "lecture" ? ` (${String(m.kind).replace(/-/g, " ")})` : "";
    return { uid, firstYmd, start: hhmm(m.start), end: hhmm(m.end), byday: days.join(","), until,
      summary: `${courseName.get(m.courseId) ?? "Class"}${kindLabel}`, room: m.room, instructor: m.instructor };
  }).sort((a, b) => (a.firstYmd < b.firstYmd ? -1 : a.firstYmd > b.firstYmd ? 1 : a.uid < b.uid ? -1 : 1));

  /** @type {Array<{ id: string, date: string, summary: string }>} the all-day assignment/exam VEVENTs */
  const dayEvents = [];
  for (const a of assignments) {
    const date = icsDate(a?.due);
    if (!date || !a.id) continue;
    dayEvents.push({ id: a.id, date, summary: `${a.title ?? "Assignment"}${suffix(a.courseId)}` });
  }
  for (const x of exams) {
    const date = icsDate(x?.date);
    if (!date || !x.id) continue;
    dayEvents.push({ id: x.id, date, summary: `${x.title ?? "Exam"}${suffix(x.courseId)}` });
  }
  // canonical order: by day, then id — output is a pure function of the input set
  dayEvents.sort((p, q) => (p.date < q.date ? -1 : p.date > q.date ? 1 : p.id < q.id ? -1 : 1));

  // CAL·5 — personal events (`pev_`). Skip-gate each through validateEvent (same tolerance as a
  // malformed meeting: dropped, never thrown), then branch on the validated shape into one of four
  // VEVENTs. Timed cases (b/d) reference the TZID, so they must extend the VTIMEZONE guard.
  let anyTimedEvent = false;
  const personalEvents = [];
  for (const ev of events) {
    if (!ev || !ev.id) continue;
    const v = validateEvent(ev);
    if (v.error) continue; // dropped, like a malformed meeting
    const f = v.fields;
    const timed = f.start !== undefined; // validated: start present ⇒ end present & end > start
    const summary = escapeText(ev.title || "Event");
    let effDate, dtLines;
    if (f.date !== undefined) {
      // one-off — (a) all-day, (b) timed
      const d = icsDate(f.date);
      if (!d) continue;
      effDate = d;
      dtLines = timed
        ? [`DTSTART;TZID=${SCHEDULE_TZID}:${d}T${hhmm(f.start)}`, `DTEND;TZID=${SCHEDULE_TZID}:${d}T${hhmm(f.end)}`]
        : [`DTSTART;VALUE=DATE:${d}`];
    } else {
      // recurring — (c) all-day, (d) timed. Anchor = earliest first-occurrence over the days (DAY_ORDER),
      // SKIP if none exists or the anchor falls past `to` (no occurrence in range).
      const days = DAY_ORDER.filter((dc) => f.days.includes(dc));
      const anchor = days.map((dc) => firstOccurrence(f.from, dc)).filter(Boolean).sort()[0];
      const untilDate = icsDate(f.to);
      if (!anchor || !untilDate || anchor > untilDate) continue;
      effDate = anchor;
      const byday = days.join(",");
      dtLines = timed
        ? [`DTSTART;TZID=${SCHEDULE_TZID}:${anchor}T${hhmm(f.start)}`, `DTEND;TZID=${SCHEDULE_TZID}:${anchor}T${hhmm(f.end)}`,
           `RRULE:FREQ=WEEKLY;BYDAY=${byday};UNTIL=${untilDate}T235959Z`] // TZID DTSTART → UTC-datetime UNTIL (like meetings)
        : [`DTSTART;VALUE=DATE:${anchor}`, `RRULE:FREQ=WEEKLY;BYDAY=${byday};UNTIL=${untilDate}`]; // DATE DTSTART → DATE UNTIL (no time, no Z)
    }
    if (timed) anyTimedEvent = true;
    personalEvents.push({ id: ev.id, effDate, summary, note: f.note, dtLines });
  }
  // canonical order: by effective date (one-off date / recurring anchor), then id
  personalEvents.sort((p, q) => (p.effDate < q.effDate ? -1 : p.effDate > q.effDate ? 1 : p.id < q.id ? -1 : 1));

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:${prodId ?? "-//school-apps//hub-calendar//EN"}`,
    "CALSCALE:GREGORIAN",
  ];
  if (name) lines.push(`X-WR-CALNAME:${escapeText(name)}`);
  // SCH·1 — the shared VTIMEZONE goes before any VEVENT that references it (RFC 5545), and ONLY when
  // something references the TZID: a meeting OR a TIMED personal event (CAL·5 cases b/d). An all-day-only
  // calendar (assignments/exams + all-day events) stays byte-identical to before — no VTIMEZONE.
  if (meetingEvents.length || anyTimedEvent) lines.push(...VTIMEZONE_CHICAGO);
  for (const e of dayEvents) {
    lines.push(
      "BEGIN:VEVENT",
      `UID:${e.id}@school-apps`, // stable: the record id IS the event identity across re-fetches
      `DTSTAMP:${dtstamp}`,
      `DTSTART;VALUE=DATE:${e.date}`, // all-day: a due date is a day, not a time
      `SUMMARY:${escapeText(e.summary)}`,
      "END:VEVENT",
    );
  }
  for (const m of meetingEvents) {
    lines.push(
      "BEGIN:VEVENT",
      `UID:${m.uid}@school-apps`,
      `DTSTAMP:${dtstamp}`,
      `DTSTART;TZID=${SCHEDULE_TZID}:${m.firstYmd}T${m.start}`, // wall-clock Central; VTIMEZONE carries the DST rules
      `DTEND;TZID=${SCHEDULE_TZID}:${m.firstYmd}T${m.end}`,
      `RRULE:FREQ=WEEKLY;BYDAY=${m.byday};UNTIL=${m.until}`,
      `SUMMARY:${escapeText(m.summary)}`,
      ...(m.room ? [`LOCATION:${escapeText(m.room)}`] : []),
      ...(m.instructor ? [`DESCRIPTION:${escapeText(m.instructor)}`] : []),
      "X-SCHOOLAPPS-KIND:meeting", // ownership marker: distinguishes class events from all-day assignments/exams
      "END:VEVENT",
    );
  }
  // CAL·5 — the personal-event block, AFTER the meetings block. SUMMARY has NO course suffix (a pev_
  // isn't course-linked); DESCRIPTION carries the note only when present; X-SCHOOLAPPS-KIND:event marks it.
  for (const ev of personalEvents) {
    lines.push(
      "BEGIN:VEVENT",
      `UID:${ev.id}@school-apps`,
      `DTSTAMP:${dtstamp}`,
      ...ev.dtLines,
      `SUMMARY:${ev.summary}`, // already escaped
      ...(ev.note ? [`DESCRIPTION:${escapeText(ev.note)}`] : []),
      "X-SCHOOLAPPS-KIND:event",
      "END:VEVENT",
    );
  }
  lines.push("END:VCALENDAR");
  return lines.map(foldLine).join(CRLF) + CRLF;
}
